import * as Yup from 'yup';
import { ApiResponseError } from '../helpers/fetch-wrapper';
import type { Companion } from './uploader-connection';
import type { Vendor } from './vendor';

// --- Vendor instructions (matches Go VendorInstructions + FormatInstructions) ---

export const FormatInstructionsSchema = Yup.object().shape({
  format: Yup.string().required(),
  label: Yup.string().required(),
  content: Yup.string().required(),
});

export type FormatInstructions = Yup.InferType<typeof FormatInstructionsSchema>;

export const VendorInstructionsSchema = Yup.object().shape({
  vendor_normalized_name: Yup.string().required(),
  // `_default/_vendor.md` ships `display_name: ""`, and any unrecognized vendor
  // key falls back to `_default`, so "" is a real response value here.
  display_name: Yup.string().defined(),
  portal_url: Yup.string().defined(),
  formats: Yup.array().of(FormatInstructionsSchema.required()).required(),
});

export type VendorInstructions = Yup.InferType<typeof VendorInstructionsSchema>;

// --- Processing result (matches receive-invoices-sync JSON response) ---

export const ReceivingFileSchema = Yup.object().shape({
  name: Yup.string().required(),
  // `.defined()`, NOT `.required()`: the API deliberately sends "" here.
  // ReceivingSession.WebView() blanks the LPOS import file's base64 payload on
  // every web response and WebSocket push (the bytes are for DynamoDB and the
  // companion, and a real one 413s API Gateway's 128 KB frame limit), keeping
  // the key so the shape stays stable. Yup's `.required()` rejects "" as
  // missing, so requiring it here failed the WHOLE session: the list handler
  // drops invalid rows, so a session in `review` vanished from
  // GET /receiving-sessions — taking "Start new receiving" and the companion
  // picker's busy check with it — while Resume and every session_state push
  // for it were rejected outright.
  content: Yup.string().defined(),
  content_hash: Yup.string().required(),
  sources: Yup.array().of(Yup.string().required()).required(),
});

export type ReceivingFile = Yup.InferType<typeof ReceivingFileSchema>;

export const ProductOrderSchema = Yup.object().shape({
  raw_item_code: Yup.string().defined(),
  item_code: Yup.string().defined(),
  suggested_item_code: Yup.string().defined(),
  barcode: Yup.string().defined(),
  brand: Yup.string().defined(),
  description: Yup.string().defined(),
  size: Yup.string().defined(),
  case_qty: Yup.number().defined(),
  cases_ordered: Yup.number().required(),
  units_ordered: Yup.number().required(),
  sales_price: Yup.number().required(),
  // Owning LiquorPOS vendor of the matched product; '' on unmatched rows and on
  // results persisted before multi-vendor receiving. .default('') is required:
  // persisted results rehydrate from IndexedDB raw, bypassing Yup casting.
  vendor_id: Yup.string().default(''),
});

export type ProductOrder = Yup.InferType<typeof ProductOrderSchema>;

export const UNKNOWN_REASON_DUPLICATE_ITEM_CODE = 'duplicate_item_code';

export const ReceivingConflictSchema = Yup.object().shape({
  product_id: Yup.string().defined(),
  brand: Yup.string().defined(),
  description: Yup.string().defined(),
  size: Yup.string().defined(),
  vendor_id: Yup.string().default(''),
});

export type ReceivingConflict = Yup.InferType<typeof ReceivingConflictSchema>;

// Unknown rows extend a parsed order with why they didn't match. Both fields
// use .default() because processing results are persisted on the session and
// rehydrated from cache raw, bypassing Yup casting — a bare .defined() would
// throw on sessions created before these fields existed. `conflicts` is
// omitempty on the Go side (absent for no_match), so undefined → [].
export const UnknownProductSchema = ProductOrderSchema.shape({
  unknown_reason: Yup.string().default(''),
  conflicts: Yup.array().of(ReceivingConflictSchema.required()).default([]),
});

export type UnknownProduct = Yup.InferType<typeof UnknownProductSchema>;

// Splits unknown rows into item-code collisions vs. everything else. `noMatch`
// is the negation so rows from older sessions (empty reason) fall through to it.
export function partitionUnknownProducts(unknowns: UnknownProduct[]) {
  return {
    duplicateItemCodes: unknowns.filter(
      (u) => u.unknown_reason === UNKNOWN_REASON_DUPLICATE_ITEM_CODE
    ),
    noMatch: unknowns.filter((u) => u.unknown_reason !== UNKNOWN_REASON_DUPLICATE_ITEM_CODE),
  };
}

export const ProductOrderMatchSchema = Yup.object().shape({
  product: ProductOrderSchema.required(),
  matched_by: Yup.string().required(),
});

export type ProductOrderMatch = Yup.InferType<typeof ProductOrderMatchSchema>;

export const UnparsedRowSchema = Yup.object().shape({
  filename: Yup.string().required(),
  row: Yup.number().required(),
  // Raw CSV cells: blank cells are "" (not missing data), so the element
  // validator must accept "". The slice itself is always non-nil
  // (`append([]string(nil), …)`). This was the Mary's Liquor drop bug.
  data: Yup.array().of(Yup.string().defined()).required(),
  err: Yup.string().defined(),
});

export type UnparsedRow = Yup.InferType<typeof UnparsedRowSchema>;

// One invoice file's extracted totals alongside the totals the file printed
// for itself, where it printed any. Mirrors Go models.ReceivingInvoiceTotals.
// The backend writes one entry per file whenever the file parsed, mismatch or
// not, which is what lets us tell "this vendor's format prints no total" from
// "this session predates the field" — both would otherwise be an absent entry.
//
// Every printed_* field is a Go pointer, so `null` on the wire for any figure
// the document doesn't print. No vendor prints a line count, which is why only
// the extracted side has one.
export const InvoiceTotalsSchema = Yup.object().shape({
  filename: Yup.string().defined(),
  printed_cases: Yup.number().nullable().default(null),
  printed_units: Yup.number().nullable().default(null),
  printed_amount: Yup.number().nullable().default(null),
  extracted_lines: Yup.number().default(0),
  extracted_cases: Yup.number().default(0),
  extracted_units: Yup.number().default(0),
  extracted_amount: Yup.number().default(0),
});

export type InvoiceTotals = Yup.InferType<typeof InvoiceTotalsSchema>;

// When Go serializes the processing result, every field is always present —
// nil slices/maps/pointers become JSON `null`. So `.nullable().defined()` is
// correct for each field here. The outer `processing_result` on the session
// is handled with `Yup.lazy` so an absent result doesn't cascade-cast into
// an empty object and blow up these inner `.defined()` checks.
export const ProcessingResultSchema = Yup.object().shape({
  receiving_file: ReceivingFileSchema.nullable().defined(),
  parse_errors: Yup.object()
    .nullable()
    .test(
      'string-values',
      'parse_errors must be a {string: string} map',
      (val) => val == null || Object.values(val).every((v) => typeof v === 'string')
    )
    .defined(),
  matched: Yup.array().of(ProductOrderMatchSchema.required()).nullable().defined(),
  not_parsed_rows: Yup.array().of(UnparsedRowSchema.required()).nullable().defined(),
  unknown_products: Yup.array().of(UnknownProductSchema.required()).nullable().defined(),
  // ORD-399. `.default(null)` rather than a bare `.defined()`: results
  // persisted before ORD-392 shipped carry no such key, and they rehydrate
  // from IndexedDB raw, bypassing Yup casting — same reason `vendor_id` and
  // `conflicts` above carry defaults. Null means "nothing to show", not
  // "nothing matched".
  invoice_totals: Yup.array().of(InvoiceTotalsSchema.required()).nullable().default(null),
});

export type ProcessingResult = Yup.InferType<typeof ProcessingResultSchema>;

// Money compares with a half-cent epsilon, matching the Go checksum tests
// (docs/superpowers/specs/2026-08-20-ord-392-invoice-totals-cross-check-design.md
// §5). SGWS rounds per-bottle prices to cents, so a summed total can land a
// fraction of a cent from the printed one with nothing actually wrong.
// Quantities compare exactly — there is no such rounding on a case count.
const MONEY_EPSILON = 0.005;

// The four figures the Review table compares, in the order it shows them.
// `lines` is only ever populated on the read side: no vendor prints a line
// count, which is the whole reason the extracted side carries one.
export type InvoiceFigures = {
  amount: number | null;
  cases: number | null;
  units: number | null;
  lines: number | null;
};

export const INVOICE_FIGURES = ['amount', 'cases', 'units', 'lines'] as const;

export type InvoiceFigure = (typeof INVOICE_FIGURES)[number];

// `matches`   — the file printed totals and we agree with all of them.
// `differs`   — the file printed totals and at least one disagrees. Warn
//               loudly, never block: a false positive must not strand a
//               delivery mid-receiving.
// `unverified` — the file prints no totals of its own (LDF, RNDC-OK, DSD,
//               BEES, generic CSV — most of the CSV volume). Deliberately not
//               a warning: it would fire on the same vendors every day and
//               train owners to ignore the region. The absent Invoice row is
//               the whole signal; the read total alone is what makes the file
//               checkable against the paper invoice.
export type InvoiceTotalsVerdict = 'matches' | 'differs' | 'unverified';

export type InvoiceTotalsSummary = {
  filename: string;
  verdict: InvoiceTotalsVerdict;
  // What we got out of the file.
  read: InvoiceFigures;
  // What the file said about itself; null per figure it doesn't print.
  printed: InvoiceFigures;
  // printed minus read, per figure, and null wherever there is nothing to
  // compare or the two agree. A positive value means the invoice claims more
  // than we found, which is the direction that under-receives stock.
  difference: InvoiceFigures;
  // Rows of this file the parser couldn't read. They are excluded from the
  // read totals on purpose (pkg/invoicereceiver/receiver.go), so they are the
  // one thing in the session that can explain a shortfall.
  unreadRows: number;
};

const epsilonFor = (figure: InvoiceFigure) => (figure === 'amount' ? MONEY_EPSILON : 0);

// A file counts as checkable if it printed ANY figure, not just the amount:
// Empire PDFs set Total Cases and Invoice Amount under independent conditions,
// and both SGWS readers parse each label separately, so cases-without-amount is
// a real wire state. Keying only on the amount would let those through
// unflagged.
function compare(read: InvoiceFigures, printed: InvoiceFigures) {
  const difference: InvoiceFigures = { amount: null, cases: null, units: null, lines: null };
  let checkable = false;

  for (const figure of INVOICE_FIGURES) {
    const p = printed[figure];
    const r = read[figure];
    if (p == null || r == null) {
      continue;
    }

    checkable = true;
    const delta = p - r;
    if (Math.abs(delta) > epsilonFor(figure)) {
      difference[figure] = delta;
    }
  }

  const differs = INVOICE_FIGURES.some((figure) => difference[figure] != null);

  return {
    difference,
    verdict: (checkable ? (differs ? 'differs' : 'matches') : 'unverified') as InvoiceTotalsVerdict,
  };
}

// One summary per invoice file in the session, ready to render. Returns []
// for a session processed before ORD-392 shipped, where the field is absent —
// those render nothing rather than a total we can't stand behind.
export function summarizeInvoiceTotals(result: ProcessingResult): InvoiceTotalsSummary[] {
  const unreadByFile = new Map<string, number>();
  for (const row of result.not_parsed_rows ?? []) {
    unreadByFile.set(row.filename, (unreadByFile.get(row.filename) ?? 0) + 1);
  }

  return (result.invoice_totals ?? []).map((totals) => {
    const read: InvoiceFigures = {
      amount: totals.extracted_amount,
      cases: totals.extracted_cases,
      units: totals.extracted_units,
      lines: totals.extracted_lines,
    };
    const printed: InvoiceFigures = {
      amount: totals.printed_amount ?? null,
      cases: totals.printed_cases ?? null,
      units: totals.printed_units ?? null,
      lines: null,
    };

    return {
      filename: totals.filename,
      read,
      printed,
      unreadRows: unreadByFile.get(totals.filename) ?? 0,
      ...compare(read, printed),
    };
  });
}

// --- Session state (matches Go models.ReceivingSessionState, post-MR-1a) ---

export const receivingSessionStates = [
  'selecting_companion', // NEW — first state in the flow
  'selecting_vendor',
  'get_invoices',
  'processing',
  'review',
  'sending_to_lpos',
  'paused_lpos_busy', // net-new: write blocked or LPOS running own receiving
  'waiting_for_lpos',
  'complete',
  'failed',
  'cancelled', // frontend-only sentinel from Phase A (ReceivingStateCancelled)
] as const;

export type ReceivingSessionState = (typeof receivingSessionStates)[number];

// Pre-Confirm states — those that precede POST /confirm (the point of no clean
// return, Invariant 5). The single TS source of truth for Cancel reachability:
// canCancelSession below and the pause screen both derive from it. Keep in sync
// with the Go mirror models.PreConfirmReceivingStates
// (lambdas/api/models/receiving_session.go).
export const PRE_CONFIRM_STATES: ReadonlySet<string> = new Set<ReceivingSessionState>([
  'selecting_companion',
  'selecting_vendor',
  'get_invoices',
  'processing',
  'review',
]);

// States from which the DELETE /cancel endpoint (clean rewind) is allowed.
// Mirrors the backend CancelReachable guard — keep in sync. A `failed` session
// is NOT cancelable: Cancel refuses terminal records (they age out via TTL), and
// the resolution path for a failure is Retry/Abandon from the session page.
// For paused_lpos_busy, Cancel is valid only when paused_from is pre-Confirm (the
// write hadn't started); post-Confirm pauses offer Abandon, not Cancel.
export const canCancelSession = (s: ReceivingSessionState, pausedFrom?: string): boolean => {
  if (PRE_CONFIRM_STATES.has(s)) {
    return true;
  }

  if (s === 'paused_lpos_busy') {
    return pausedFrom != null && PRE_CONFIRM_STATES.has(pausedFrom);
  }

  return false;
};

// /cancel answers 409 for several unrelated reasons and the client recovers
// from each differently, so the body's `code` is what tells them apart —
// mirrors SessionHeldByOtherTabCode in lambdas/api/api.go. Never infer the
// cause from the message text: the prose is copy, not contract.
const SESSION_HELD_BY_OTHER_TAB_CODE = 'SESSION_HELD_BY_OTHER_TAB';

/**
 * True when cancel was refused because another tab still has the session open
 * and connected — a normal outcome the user resolves themselves, not a defect.
 *
 * Lives here beside canCancelSession because it is domain knowledge about the
 * wire contract, not transport: it needs no FetchWrapper and mirrors
 * SessionHeldByOtherTabCode, exactly as canCancelSession mirrors the backend's
 * CancelReachable. services/receiving is transport-only, which is what lets
 * every router suite stub it wholesale (services/__mocks__/receiving.ts) — a
 * predicate defined there would be stubbed out of the very cancel path under
 * test, which is how the wrong home first showed up.
 *
 * Callers show a warning and skip error reporting for it, so this stays strict:
 * status *and* code together. Anything else gets reported normally.
 */
export const isSessionHeldByOtherTab = (error: unknown): boolean =>
  error instanceof ApiResponseError &&
  error.status === 409 &&
  error.body?.code === SESSION_HELD_BY_OTHER_TAB_CODE;

// States from which Abandon (POST /abandon) is reachable (spec §7, §10).
export const canAbandonSession = (s: ReceivingSessionState): boolean =>
  s === 'failed' || s === 'paused_lpos_busy' || s === 'waiting_for_lpos';

// States in which the given field is populated on the wire. Used by the
// session schema to turn requirements on/off as the state machine progresses.
// `failed` and `cancelled` can occur from any prior state, so their field
// shape depends on what was set before — we don't enforce those strictly.
const STATES_WITH_VENDOR: ReceivingSessionState[] = [
  'get_invoices',
  'processing',
  'review',
  'sending_to_lpos',
  'waiting_for_lpos',
  'complete',
];

const STATES_WITH_PROCESSING_RESULT: ReceivingSessionState[] = [
  'review',
  'sending_to_lpos',
  'waiting_for_lpos',
  'complete',
];

export const receivingSessionFileSources = ['web_upload', 'companion_detected'] as const;

export type ReceivingSessionFileSource = (typeof receivingSessionFileSources)[number];

// Mirrors Go models.ReceivingSessionFile.
export const ReceivingSessionFileSchema = Yup.object().shape({
  filename: Yup.string().required(),
  source: Yup.string<ReceivingSessionFileSource>()
    .oneOf([...receivingSessionFileSources])
    .required(),
  s3_key: Yup.string().required(),
  size: Yup.number().required(),
});

export type ReceivingSessionFile = Yup.InferType<typeof ReceivingSessionFileSchema>;

// Mirrors Go models.ReceivingSession. Intentionally NO `created_at` — the Go
// struct only carries `updated_at` and `ttl`.
//
// Which fields are present depends on the session state; fields become
// required as the state machine progresses. `processing_result` uses
// `Yup.lazy` so an absent value isn't cast to `{}` and cascaded into the
// inner required-field checks.
export const ReceivingSessionSchema = Yup.object().shape({
  store_id: Yup.string().required(),
  session_id: Yup.string().required(),
  state: Yup.string<ReceivingSessionState>()
    .oneOf([...receivingSessionStates])
    .required(),
  machine_id: Yup.string().when('state', ([state], schema) =>
    state === 'selecting_companion' || state === 'cancelled' || state === 'failed'
      ? schema.optional()
      : schema.required()
  ),
  vendor_id: Yup.string().when('state', ([state], schema) =>
    STATES_WITH_VENDOR.includes(state as ReceivingSessionState)
      ? schema.required()
      : schema.optional()
  ),
  // The full vendor set. Empty on sessions written before multi-vendor
  // receiving — those carry only the scalar vendor_id, and live up to 24h.
  vendor_ids: Yup.array().of(Yup.string().required()).default([]),
  files: Yup.array().of(ReceivingSessionFileSchema.required()).optional(),
  processing_result: Yup.lazy((value, options) => {
    const state = (options.parent as { state?: ReceivingSessionState } | undefined)?.state;
    if (value == null) {
      // Absent or null is allowed unless state says it must be present.
      // Returning a non-object schema here avoids Yup's ObjectSchema casting
      // `undefined` → `{}`, which would otherwise fail the inner
      // `.defined()` checks on ProcessingResultSchema.
      const mustBePresent = state && STATES_WITH_PROCESSING_RESULT.includes(state);

      return mustBePresent
        ? Yup.mixed().required('processing_result is required in this state')
        : Yup.mixed().nullable();
    }

    return ProcessingResultSchema;
  }),
  error: Yup.string().when('state', ([state], schema) =>
    state === 'failed' ? schema.required() : schema.optional()
  ),
  failed_from: Yup.string().when('state', ([state], schema) =>
    state === 'failed' ? schema.required() : schema.optional()
  ),

  // Net-new fields (Rev 3 / B16): timestamps + pause cause + content hash.
  // All optional on the wire — absent when state hasn't reached that point yet.
  paused_from: Yup.string().optional(),
  paused_since: Yup.string().optional(),
  waiting_since: Yup.string().optional(),
  content_hash: Yup.string().optional(),

  active_viewer_tab_id: Yup.string().optional(),
  updated_at: Yup.string().required(),
  ttl: Yup.number().required(),
});

export type ReceivingSession = Yup.InferType<typeof ReceivingSessionSchema>;

// Vendor objects a session actually spans: the vendor_ids multi-vendor set
// when present, falling back to the single legacy vendor_id — sessions
// written before ORD-395 multi-vendor receiving carry only the scalar field.
// Single source of truth for the fallback rule, used by both the review
// header's vendor name/count and the Vendor provenance column's id→name map.
export function sessionVendors(
  session: ReceivingSession | null | undefined,
  vendors: Vendor[]
): Vendor[] {
  if (!session) {
    return [];
  }

  const ids = session.vendor_ids?.length
    ? session.vendor_ids
    : session.vendor_id
      ? [session.vendor_id]
      : [];

  return ids
    .map((id) => vendors.find((v) => v.vendor_id === id))
    .filter((v): v is Vendor => v !== undefined);
}

// A companion can't take on a new or resumed session while it already has an
// active session assigned, or while its disk still holds a pending LPOS import
// file (rcv_blocked). Single source of truth for "is this companion free?",
// used by both the receiving list page (enabling "Start new receiving" /
// Resume) and the in-session companion picker — keep the two in sync.
export function makeCompanionBusyPredicate(
  activeSessions: ReceivingSession[]
): (companion: Companion) => boolean {
  const machineIdsWithSession = new Set(
    activeSessions.map((s) => s.machine_id).filter((id): id is string => id != null && id !== '')
  );

  return (companion) =>
    machineIdsWithSession.has(companion.uploader.machine_id) ||
    companion.uploader.rcv_blocked === true;
}
